import {
  Box,
  ClickAwayListener,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import {
  Control,
  useFieldArray,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { IconButton, ThumbnailUploadField } from 'utoniq-ui'

import { ItemType } from '@/clients/utoniq-core/schema'
import {
  CreateItemProps,
  FileUploadInfo,
} from '@/components/pages/ConsoleItemCreate/template/ItemCreateForm'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type ItemImageInputCellProps = {
  setValue: UseFormSetValue<CreateItemProps>
  watch: UseFormWatch<CreateItemProps>
  control: Control<CreateItemProps>
  fileUploadInfo: FileUploadInfo
}

export const ItemImageInputCell: Cell<ItemImageInputCellProps> = ({
  fileUploadInfo,
  watch,
  control,
  setValue,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const { fields } = useFieldArray({ control, name: 'images', keyName: '_id' })
  const [open, setOpen] = useState(false)
  const images = watch('images')
  const indexAdd = useMemo(
    () => images?.findIndex(({ tmpPath }) => !tmpPath) ?? 0,
    [images]
  )
  return (
    <CellFrame sx={{ pb: 8 }}>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">{t('imagesLabel')}</Typography>

          <Tooltip
            title={t('imagesDesc')}
            onClose={() => setOpen(false)}
            open={open}
            disableHoverListener
            disableTouchListener
            arrow
            slotProps={{
              popper: {
                disablePortal: true,
              },
            }}
          >
            <IconButton.Info onClick={() => setOpen(true)} />
          </Tooltip>
        </Box>
      </ClickAwayListener>

      <Stack sx={{ py: 4, overflowY: 'auto', ...styleSets.hideScrollbar() }}>
        <Stack direction="row" spacing={2}>
          {fields.map((field: { _id: string }, index) => (
            <UploadImage
              index={index}
              key={field._id}
              fileUploadInfo={fileUploadInfo}
              setValue={setValue}
              watch={watch}
              readOnly={index > indexAdd}
            />
          ))}
        </Stack>
      </Stack>
    </CellFrame>
  )
}

type UploadImageProps = {
  index: number
  readOnly: boolean
  setValue: ItemImageInputCellProps['setValue']
  watch: ItemImageInputCellProps['watch']
  fileUploadInfo: ItemImageInputCellProps['fileUploadInfo']
}
const UploadImage: FC<UploadImageProps> = ({
  index,
  readOnly,
  watch,
  setValue,
  fileUploadInfo,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('')
  const thumbnailWatch = watch(`images.${index}.tmpPath`)

  useEffect(() => {
    setThumbnailUrl(thumbnailWatch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <CellFrame sx={{ position: 'relative', width: 'fit-content' }}>
      <Box
        sx={{
          position: 'absolute',
          bottom: 4,
          right: 4,
          bgcolor: 'grey.100',
          borderRadius: '40px',
          p: '2px 4px',
          zIndex: 2,
        }}
      >
        <Typography variant="overline" component="p">
          {index + 1}/30
        </Typography>
      </Box>
      <ThumbnailUploadField
        t={t}
        shape="square"
        readOnly={readOnly}
        thumbnailProps={{
          src: thumbnailUrl,
          sx: { img: { objectFit: 'contain' } },
        }}
        onClose={() => {
          setThumbnailUrl('')
          setValue(`images.${index}.tmpPath`, '' as never, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }}
        fileUploadInfo={{
          type: 'image',
          progress: fileUploadInfo.progress,
          onDrop() {
            return fileUploadInfo.onDrop(ItemType.Image).then(resource => {
              if (!resource) return
              setThumbnailUrl(resource.imageUrl ?? '')
              setValue(
                `images.${index}.tmpPath`,
                resource.imageUploadPath as never,
                { shouldValidate: true, shouldDirty: true }
              )
              return resource
            })
          },
        }}
      />
    </CellFrame>
  )
}
